import React, { useEffect, useState, forwardRef } from "react";
import ComplianceChart from "./ComplianceChart";
import MsbViolationsChart from "./MsbViolationsChart";
import RiskScoreChart from "./RiskScoreChart";
import ChangesByDateChart from "./ChangesByDateChart";
import ViolationsByDeviceChart from "./ViolationsByDeviceChart";
import StatusChart from "./StatusChart";
import DatePicker from "react-datepicker";
import { AuditScope } from "./chartDynamicLabels";
import SelectV2 from "../view/SelectV2";

const scopeOptions = [
    { value: AuditScope.BY_DAY, label: "Last Day" },
    { value: AuditScope.BY_WEEK, label: "Last Week" },
    { value: AuditScope.BY_MONTH, label: "Last Month" },
    { value: AuditScope.CUSTOM, label: "Custom" },
];

const getDateDaysAgo = (days) => {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date;
};

const getDateRangeByScope = (selectedScope) => {
    const today = new Date();

    switch (selectedScope) {
        case AuditScope.BY_WEEK:
            return [getDateDaysAgo(6), today];
        case AuditScope.BY_MONTH:
            return [getDateDaysAgo(29), today];
        case AuditScope.BY_DAY:
        default:
            return [getDateDaysAgo(1), today];
    }
};

function ChartSection() {

    const [scope, setScope] = useState(AuditScope.BY_DAY);
    const initialDateRange = getDateRangeByScope(AuditScope.BY_DAY);
    const [selectedDateFrom, setSelectedDateFrom] = useState(initialDateRange[0]);
    const [selectedDateTo, setSelectedDateTo] = useState(initialDateRange[1]);

    // Combined date range state for the single input
    const [dateRange, setDateRange] = useState([selectedDateFrom, selectedDateTo]);

    useEffect(() => {
        if (scope !== AuditScope.CUSTOM) {
            const presetRange = getDateRangeByScope(scope);
            setDateRange(presetRange);
            setSelectedDateFrom(presetRange[0]);
            setSelectedDateTo(presetRange[1]);
        }
    }, [scope]);

    // Update individual states only when range is complete (both start and end dates selected)
    const handleDateRangeChange = (update) => {
        if (scope !== AuditScope.CUSTOM) {
            return;
        }

        setDateRange(update);
        if (update[0] && update[1]) {
            setSelectedDateFrom(update[0]);
            setSelectedDateTo(update[1]);
        }
    };

    // const getDatepickerDates = () => {
    //     return logSource.name === 'Syslog' ? availableDates.syslog : availableDates.app
    // }

    const DatePickerInput = forwardRef(({ value, onClick, disabled }, ref) => (
        <button type="button" className="btn oct_btn-datefrom-v2" onClick={onClick} ref={ref} disabled={disabled}>
            {value}
        </button>
    ));

    return (
        <div className="oct_charts">

            <div className="container-fluid">
                <div className="row justify-content-end align-items-center mt-2 mb-4">
                    <div className="dashboard_filters_wrapper">
                        <div className="dashboard_scope_select">
                            <SelectV2
                                inputId="dashboardScope"
                                className="dashboard_scope_select_control"
                                value={scope}
                                onChange={setScope}
                                options={scopeOptions}
                                isSearchable={false}
                            />
                        </div>
                        <div className={`dashboard_calendar_setting ${scope === AuditScope.CUSTOM ? 'is-enabled' : 'is-disabled'}`} >
                            <div className="oct_form-group">
                                <DatePicker
                                    selectsRange={true}
                                    startDate={dateRange[0]}
                                    endDate={dateRange[1]}
                                    onChange={handleDateRangeChange}
                                    disabled={scope !== AuditScope.CUSTOM}
                                    // includeDates={getDatepickerDates()}
                                    customInput={<DatePickerInput />}
                                    placeholderText="Select date range"
                                    dateFormat="MM/dd/yyyy"
                                    shouldCloseOnSelect={false}
                                    maxDate={new Date()}
                                    popperPlacement="bottom-end"
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <ComplianceChart
                            fromDate={selectedDateFrom}
                            toDate={selectedDateTo || selectedDateFrom} // Handle null case
                            scope={scope} />
                    </div>
                    <div className="col-md-6">
                        <MsbViolationsChart
                            fromDate={selectedDateFrom}
                            toDate={selectedDateTo || selectedDateFrom} // Handle null case
                            scope={scope} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <RiskScoreChart
                            fromDate={selectedDateFrom}
                            toDate={selectedDateTo || selectedDateFrom} // Handle null case
                            scope={scope} />
                    </div>
                    <div className="col-md-6">
                        <ChangesByDateChart
                            fromDate={selectedDateFrom}
                            toDate={selectedDateTo || selectedDateFrom} // Handle null case
                            scope={scope} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-md-6">
                        <ViolationsByDeviceChart
                            fromDate={selectedDateFrom}
                            toDate={selectedDateTo || selectedDateFrom} // Handle null case
                            scope={scope} />
                    </div>
                    <div className="col-md-6">
                        <StatusChart />
                    </div>
                </div>
            </div>
        </div>
    )

}

export default ChartSection;
